import { NODE_LABELS } from "../../lib/operationPorts.js";
import type { OperationGraphNodeType } from "../../lib/operationPorts.js";
import { isOutOfChainTargetContext, isRunTargetNode, runContextLabel } from "../../lib/operationRun.js";
import type { OperationRun, OperationRunNode, RunNodeStatus } from "../../lib/operationRun.js";

// ADR-0128 — Run detail surface. Renders the per-node breakdown of a run: node
// label (never the node id/UUID), state, rows, duration, and a
// "Served from cache — last run <absolute time>" line for `from_cache` nodes.
// This is the canonical answer to "where do I see fromCache".

type NodeLabelLookup = (nodeId: string) => { label: string | null; nodeType: OperationGraphNodeType | null };

const NODE_STATUS_LABELS: Record<RunNodeStatus, string> = {
  waiting: "Queued",
  running: "Running",
  from_cache: "From cache",
  succeeded: "Succeeded",
  failed: "Failed",
  skipped: "Skipped",
  cancelled: "Cancelled", // ADR-0130
};

const NODE_STATUS_PILL: Record<RunNodeStatus, string> = {
  waiting: "pill pillNeutral",
  running: "pill pillWarn",
  from_cache: "pill",
  succeeded: "pill pillOk",
  failed: "pill pillBad",
  skipped: "pill pillNeutral",
  cancelled: "pill pillNeutral", // ADR-0130
};

const RUN_STATUS_LABELS: Record<OperationRun["status"], string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
};

function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Absolute timestamp only — house rule (never relative). */
function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function RunDetailPanel(props: {
  run: OperationRun;
  nodes: OperationRunNode[];
  nodeLabel: NodeLabelLookup;
  /** Optional — surfaced while the run is active. */
  onStop?: () => void;
  /**
   * ADR-0130: when set (Trace opened from a node's History), display only these
   * nodes — the focus node ∪ its upstream lineage. A trace never shows nodes
   * downstream of its subject. Run-level openings omit it (full chain).
   */
  focusNodeIds?: ReadonlySet<string> | null;
}) {
  const { run, nodeLabel, onStop, focusNodeIds } = props;
  const nodes = focusNodeIds ? props.nodes.filter((n) => focusNodeIds.has(n.node_id)) : props.nodes;
  const active = run.status === "queued" || run.status === "running";

  function labelFor(node: OperationRunNode): string {
    const looked = nodeLabel(node.node_id);
    if (looked.label && looked.label.trim()) return looked.label;
    // Fall back to the human node-type label — never the node id/UUID.
    const nt = (looked.nodeType ?? (node.node_type as OperationGraphNodeType)) as OperationGraphNodeType;
    return NODE_LABELS[nt] ?? node.node_type;
  }

  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 700 }}>Run</span>
          <span className="muted" style={{ fontSize: 12 }}>{runContextLabel(run.context)}</span>
          <span className={
            run.status === "succeeded" ? "pill pillOk"
              : run.status === "failed" ? "pill pillBad"
              : run.status === "cancelled" ? "pill pillNeutral"
              : "pill pillWarn"
          }>
            {RUN_STATUS_LABELS[run.status]}
          </span>
          {run.requested_by_name ? (
            <span className="muted" style={{ fontSize: 12 }}>by {run.requested_by_name}</span>
          ) : null}
        </div>
        {active && onStop ? (
          <button className="btn btnSm" type="button" onClick={() => onStop()}>Stop</button>
        ) : null}
      </div>

      {run.error ? (
        <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>{run.error}</div>
      ) : null}

      <div className="tableWrap">
        <table className="table tableCompact">
          <thead>
            <tr>
              <th>Node</th>
              <th>State</th>
              <th style={{ textAlign: "right" }}>Rows</th>
              <th style={{ textAlign: "right" }}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((n) => (
              <tr key={n.node_id}>
                <td>{labelFor(n)}</td>
                <td>
                  <span className={NODE_STATUS_PILL[n.status]}>{NODE_STATUS_LABELS[n.status]}</span>
                  {n.status === "from_cache" ? (
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {n.cache_produced_at
                        ? `Served from cache — data as of ${formatTime(n.cache_produced_at)}`
                        : "Served from cache"}
                    </div>
                  ) : null}
                  {n.status === "failed" && n.error ? (
                    <div className="help" style={{ color: "var(--danger-text)", marginTop: 2 }}>{n.error}</div>
                  ) : null}
                  {n.status === "skipped" && isOutOfChainTargetContext(run.context) && isRunTargetNode(run, n.node_id) ? (
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Loaded outside the step chain — every load records this step as skipped. The run's own
                      outcome above is the load's outcome.
                    </div>
                  ) : null}
                </td>
                <td style={{ textAlign: "right" }}>{n.row_count !== null ? n.row_count.toLocaleString() : "—"}</td>
                <td style={{ textAlign: "right" }}>{formatDuration(n.duration_ms)}</td>
              </tr>
            ))}
            {nodes.length === 0 ? (
              <tr><td colSpan={4} className="muted">No node activity yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
